import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useSEOMeta, generateBreadcrumbSchema, generateWebPageSchema } from "@/components/SEO";

const coreSectors = [
  {
    icon: "ri-heart-pulse-line",
    title: "Health",
    desc: "Providing essential healthcare services, medical supplies, and health education to displaced populations and host communities.",
    color: "bg-rose-50 text-rose-600 border-rose-100",
    iconBg: "bg-rose-100",
  },
  {
    icon: "ri-shield-user-line",
    title: "Protection (Gender-Based Violence)",
    desc: "Combating gender-based violence through awareness campaigns, legal support, and safe spaces for survivors.",
    color: "bg-amber-50 text-amber-600 border-amber-100",
    iconBg: "bg-amber-100",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "Economic Security",
    desc: "Empowering individuals with livelihood skills, micro-financing, and entrepreneurship training for self-reliance.",
    color: "bg-emerald-50 text-emerald-600 border-emerald-100",
    iconBg: "bg-emerald-100",
  },
  {
    icon: "ri-book-open-line",
    title: "Education",
    desc: "Ensuring access to quality education for children and youth affected by displacement through school construction and scholarships.",
    color: "bg-sky-50 text-sky-600 border-sky-100",
    iconBg: "bg-sky-100",
  },
];



const sdgGoals = [
  { number: "SDG 1", title: "No Poverty", icon: "ri-hand-coin-line" },
  { number: "SDG 3", title: "Good Health", icon: "ri-heart-pulse-line" },
  { number: "SDG 4", title: "Quality Education", icon: "ri-book-open-line" },
  { number: "SDG 5", title: "Gender Equality", icon: "ri-women-line" },
  { number: "SDG 6", title: "Clean Water", icon: "ri-drop-line" },
  { number: "SDG 10", title: "Reduced Inequalities", icon: "ri-scales-line" },
  { number: "SDG 16", title: "Peace & Justice", icon: "ri-shield-check-line" },
  { number: "SDG 17", title: "Partnerships", icon: "ri-links-line" },
];

export default function EkaFoundationPage() {
  useSEOMeta({
    title: "EKA David Memorial Foundation - BHODEV Partner | SDG-Aligned Humanitarian Work",
    description:
      "EKA David Memorial Foundation partners with BHODEV Cameroon to advance UN Sustainable Development Goals. Together addressing health, protection, economic security, and education for displaced communities.",
    keywords:
      "EKA Foundation, EKA David Memorial Foundation, BHODEV partner, SDG Cameroon, humanitarian foundation Cameroon, NGO partnership Cameroon, sustainable development Cameroon",
    canonicalUrl: "https://bhodevcameroon.org/eka-foundation",
    ogImage:
      "https://readdy.ai/api/search-image?query=memorial%20foundation%20humanitarian%20work%20community%20development%20Africa%20Cameroon%20warm%20golden%20light%20landscape%20wide%20panoramic%20professional%20NGO%20organization%20sustainable%20development%20goals&width=1200&height=630&seq=eka-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Partners & Donors", url: "https://bhodevcameroon.org/partners-donors" },
        { name: "EKA Foundation", url: "https://bhodevcameroon.org/eka-foundation" },
      ]),
      ...generateWebPageSchema(
        "EKA David Memorial Foundation - BHODEV Partner",
        "EKA David Memorial Foundation partners with BHODEV Cameroon to advance UN Sustainable Development Goals.",
        "https://bhodevcameroon.org/eka-foundation"
      ),
    },
  });

  const navigate = useNavigate();
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation();
  const { ref: whatRef, isVisible: whatVisible } = useScrollAnimation();
  const { ref: sdgRef, isVisible: sdgVisible } = useScrollAnimation();
  const { ref: sectorsRef, isVisible: sectorsVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[500px] md:h-[580px] flex items-center justify-center overflow-hidden">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frenovation-and-reconstruction-of-bomaka-secondary-school-buildings%2FIMG-20231209-WA0067.jpg?alt=media&token=0dc5b1d0-5333-4f67-b3c8-0a9c1cd8d79c"
          alt="Eka-David Memorial Foundation"
          title="EKA David Memorial Foundation - BHODEV Partner for SDG"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70"></div>
        <div
          ref={heroRef}
          className={`relative z-10 text-center px-4 w-full transition-all duration-1000 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <nav className="flex items-center justify-center gap-2 text-white/60 text-xs mb-5">
            <a href="/" className="hover:text-white transition-colors cursor-pointer">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/partners-donors" className="hover:text-white transition-colors cursor-pointer">Partners &amp; Donors</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">EKA Foundation</span>
          </nav>
          <span className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary text-xs font-semibold px-4 py-1.5 rounded-full mb-5 backdrop-blur-sm">
            <i className="ri-award-line"></i> Foundation Partner
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight font-playfair">
            EKA DAVID MEMORIAL<br />Foundation
          </h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            A step towards becoming compliant with UN Sustainable Development Goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <a
              href="#what-is-eka"
              className="bg-primary text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 shadow-lg shadow-primary/30 whitespace-nowrap cursor-pointer"
            >
              Learn More
            </a>
            <button
              onClick={() => navigate("/partners-donors")}
              className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              All Partners
            </button>
          </div>
        </div>
      </section>

      {/* Intro strip */}
      <section className="bg-primary py-5 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/90 text-sm text-center md:text-left font-medium">
            EKA Foundation — Aligned with the UN Sustainable Development Goals for a better, more equitable world.
          </p>
          <div className="flex items-center gap-6">
            {["SDG 1", "SDG 3", "SDG 5", "SDG 16", "SDG 17"].map((sdg) => (
              <span key={sdg} className="text-white/70 text-xs font-bold border border-white/30 px-2.5 py-1 rounded-full whitespace-nowrap">
                {sdg}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Foundation Intro — split layout */}
      <section id="what-is-eka" className="py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            ref={introRef}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-14 items-center transition-all duration-700 ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {/* Text */}
            <div>
              <span className="text-primary text-sm font-semibold tracking-widest uppercase">Partners &amp; Donors</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-2 font-playfair leading-tight">
                EKA DAVID MEMORIAL Foundation
              </h2>
              <p className="text-primary text-sm font-medium mb-6 italic">
                EKA Foundation — A step towards becoming compliant with UN Sustainable Development Goals.
              </p>
              <div className="space-y-4 text-stone-600 text-sm leading-relaxed">
                <p>
                  Complex problem solving requires fresh ideas, innovative thinking, and a willingness to cooperate in new ways.
                </p>
                <p>
                  Even though the world is improving overall, many large-scale challenges still need to be tackled. The global refugee-crisis affects all layers of society — devastating the lives of those of displacement and adding massive pressure of the hosting countries and delaying economic growth and prosperity for the countries affected by conflict, which ultimately affects the world as a whole.
                </p>
                <p>
                  Dynamic partnerships, in the spirit of the Sustainable Development Goals, between non-governmental organizations, duty bearers and private foundations and companies can and will become an important part of the solution.
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="#join-forces"
                  className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-dark transition-all duration-300 shadow-lg shadow-primary/25 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shake-hands-line"></i> Join Forces
                </a>
                <a
                  href="/our-work"
                  className="inline-flex items-center gap-2 border border-stone-200 text-stone-700 px-6 py-3 rounded-full text-sm font-semibold hover:border-primary hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-folder-open-line"></i> View Projects
                </a>
              </div>
            </div>

            {/* Image + logo placeholder */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden h-80 w-full">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frehabilitation-of-water-catchment-in-wonya-mavio%2FIMG-20230706-WA0016.jpg?alt=media&token=fa66effd-bb1b-4c7c-8b42-fdcc5e723c77"
                  alt="EKA David Memorial Foundation"
                  title="EKA David Memorial Foundation - SDG Compliant Humanitarian Foundation"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Company logo placeholder */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-lg border border-stone-100 flex flex-col items-center justify-center w-32 h-20">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-xl mb-1">
                  <i className="ri-award-fill text-primary text-xl"></i>
                </div>
                <span className="text-stone-700 text-xs font-bold text-center leading-tight">EKA Foundation</span>
              </div>
              <div className="absolute -top-4 -right-4 bg-primary rounded-2xl px-4 py-3 text-white text-center">
                <div className="text-xl font-bold font-playfair">SDG</div>
                <div className="text-xs text-white/80">Compliant</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is EKA Foundation */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div
            ref={whatRef}
            className={`transition-all duration-700 ${whatVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              <div>
                <span className="text-primary text-sm font-semibold tracking-widest uppercase">About the Foundation</span>
                <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-6 font-playfair">What is EKA Foundation?</h2>
                <div className="space-y-4 text-stone-600 text-sm leading-relaxed">
                  <p>
                    Complex problem solving requires fresh ideas, innovative thinking, and a willingness to cooperate in new ways.
                  </p>
                  <p>
                    Even though the world is improving overall, many large-scale challenges still need to be tackled. The global refugee-crisis affects all layers of society — devastating the lives of those of displacement and adding massive pressure of the hosting countries and delaying economic growth and prosperity for the countries affected by conflict, which ultimately affects the world as a whole.
                  </p>
                  <p>
                    Dynamic partnerships, in the spirit of the Sustainable Development Goals, between non-governmental organizations, duty bearers and private foundations and companies can and will become an important part of the solution.
                  </p>
                </div>

                <div className="mt-8 p-5 rounded-2xl bg-primary/5 border border-primary/15">
                  <h4 className="font-bold text-stone-900 text-sm mb-2 flex items-center gap-2">
                    <i className="ri-information-line text-primary"></i> What&apos;s in it for me?
                  </h4>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    For many companies, taking an interest in wider societal issues is not just a CSR initiative, but a fundamental part of making good business. Operating sustainably and making a positive impact has become key value margins for not only consumers but an important motivator for employees as well.
                  </p>
                </div>
              </div>

              <div id="join-forces">
                <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-8 text-white mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-4">
                    <i className="ri-shake-hands-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3 font-playfair">Let&apos;s Join Forces in a Partnership</h3>
                  <p className="text-white/80 text-sm leading-relaxed mb-2">
                    When joining forces with us, your company can tap into a wide range of programs and agenda-setting topics: human rights, climate change initiatives, education and self-reliance programs, food &amp; water programs, LGBT+ rights and many more!
                  </p>
                  <p className="text-white/80 text-sm leading-relaxed">
                    The level of engagement is completely up to you and our partnership can be tailored to fit your company DNA and strategic focus.
                  </p>
                </div>

                <div className="bg-stone-900 rounded-2xl p-8 text-white">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl mb-4">
                    <i className="ri-earth-line text-2xl text-primary"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3 font-playfair">BHODEV &amp; EKA Foundation</h3>
                  <p className="text-stone-400 text-sm leading-relaxed mb-3">
                    BHODEV strongly believes in the value of collaborative action, knowledge sharing and partnerships to improve protection and response work for people affected by displacement.
                  </p>
                  <p className="text-stone-400 text-sm leading-relaxed">
                    Work related to climate and environment is no exception. We look to join forces, work in partnerships and create positive-sum games to ensure that needs of people affected by displacement, planet, peace and prosperity are balanced for sustainable outcomes for all.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SDG Alignment */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            ref={sdgRef}
            className={`transition-all duration-700 ${sdgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="text-center mb-12">
              <span className="text-primary text-sm font-semibold tracking-widest uppercase">UN Agenda 2030</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-4 font-playfair">
                Aligned with the Sustainable Development Goals
              </h2>
              <p className="text-stone-500 max-w-2xl mx-auto text-sm leading-relaxed">
                EKA Foundation&apos;s work is directly aligned with the United Nations&apos; 2030 Agenda for Sustainable Development, contributing to a more equitable and peaceful world.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {sdgGoals.map((goal, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center text-center p-5 rounded-2xl bg-stone-50 hover:bg-secondary/30 transition-colors duration-300 border border-stone-100 hover:border-primary/20 group"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-xl mb-3 group-hover:bg-primary/20 transition-colors duration-300">
                    <i className={`${goal.icon} text-primary text-xl`}></i>
                  </div>
                  <span className="text-primary text-xs font-bold mb-1">{goal.number}</span>
                  <span className="text-stone-700 text-xs font-semibold">{goal.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4 Core Sectors */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div
            ref={sectorsRef}
            className={`transition-all duration-700 ${sectorsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              <div>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frenovation-and-reconstruction-of-bomaka-nursery-school-buildings%2FIMG_6491%20-%20BOGI%20HEALTH%20ORGANIZATION%20FOR%20DEVELOPMENT%20-%20BHODEV.jpeg?alt=media&token=6b1a86fe-b9e1-4865-8a81-811b2b92df7f"
                  alt="4 Core Sectors"
                  title="BHODEV 4 Core Sectors - Health, Education, Economic Security & Protection"
                  className="w-full h-[420px] object-cover object-top rounded-2xl"
                />
              </div>
              <div>
                <span className="text-primary text-sm font-semibold tracking-widest uppercase">Our Focus Areas</span>
                <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-4 font-playfair">4 Core Sectors</h2>
                <p className="text-stone-500 text-sm leading-relaxed mb-8">
                  We work globally providing support and solutions for refugees and displaced people within four core sectors. Learn more about the five core sectors and our work within each of them.
                </p>
                <div className="space-y-4">
                  {coreSectors.map((sector, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-4 p-4 rounded-xl border ${sector.color} transition-all duration-300 hover:scale-[1.01]`}
                    >
                      <div className={`w-10 h-10 flex items-center justify-center ${sector.iconBg} rounded-lg shrink-0`}>
                        <i className={`${sector.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-stone-900 text-sm mb-1">{sector.title}</h4>
                        <p className="text-stone-500 text-xs leading-relaxed">{sector.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* CTA */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div
            ref={ctaRef}
            className={`transition-all duration-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=community%20partnership%20collaboration%20sustainable%20development%20Africa%20Cameroon%20warm%20golden%20sunset%20landscape%20wide%20panoramic%20NGO%20humanitarian%20work&width=1200&height=380&seq=eka-cta-1&orientation=landscape"
                alt="Partner with EKA Foundation"
                title="Partner with EKA Foundation & BHODEV"
                className="w-full h-64 md:h-72 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-14">
                <span className="text-primary text-xs font-semibold tracking-widest uppercase mb-3">Get Involved</span>
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 font-playfair max-w-lg">
                  Partner with EKA Foundation &amp; BHODEV
                </h2>
                <p className="text-white/70 text-sm max-w-md mb-6 leading-relaxed">
                  Dynamic partnerships between NGOs, duty bearers and private foundations can and will become an important part of the solution to the global refugee crisis.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/contact"
                    className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-dark transition-all duration-300 shadow-lg shadow-primary/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-send-line"></i> Contact Us
                  </a>
                  <a
                    href="/get-involved"
                    className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-team-line"></i> Get Involved
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
